export interface PaisTelefone {
  /** ISO-2, o mesmo código que o resto do sistema usa. */
  codigo: string;
  nome: string;
  bandeira: string;
  ddi: string;
  /** `9` é dígito; o resto é literal. O Brasil tem regra própria, abaixo. */
  mascara: string;
  exemplo: string;
}

/**
 * Os cinco que aparecem num evento corporativo brasileiro — e nada além.
 *
 * A lista curta é a escolha: 193 países num seletor transformam o caso comum
 * (um celular de São Paulo) numa busca. Quem precisar de outro DDI tem a opção
 * "Outro país", que aceita o número completo com `+`.
 */
export const PAISES_TELEFONE: PaisTelefone[] = [
  {
    codigo: 'BR',
    nome: 'Brasil',
    bandeira: '🇧🇷',
    ddi: '55',
    mascara: '(99) 99999-9999',
    exemplo: '(11) 98765-4321',
  },
  {
    codigo: 'US',
    nome: 'Estados Unidos',
    bandeira: '🇺🇸',
    ddi: '1',
    mascara: '(999) 999-9999',
    exemplo: '(415) 555-0132',
  },
  {
    codigo: 'PT',
    nome: 'Portugal',
    bandeira: '🇵🇹',
    ddi: '351',
    mascara: '999 999 999',
    exemplo: '912 345 678',
  },
  {
    codigo: 'AR',
    nome: 'Argentina',
    bandeira: '🇦🇷',
    ddi: '54',
    mascara: '(99) 9999-9999',
    exemplo: '(11) 2345-6789',
  },
  {
    codigo: 'MX',
    nome: 'México',
    bandeira: '🇲🇽',
    ddi: '52',
    mascara: '(99) 9999-9999',
    exemplo: '(55) 1234-5678',
  },
];

/** Quantos dígitos a máscara comporta. */
function capacidade(mascara: string) {
  return (mascara.match(/9/g) ?? []).length;
}

/** Encaixa os dígitos digitados no molde do país. */
export function aplicarMascara(valor: string, mascara: string) {
  const digitos = valor.replace(/\D/g, '').slice(0, capacidade(mascara));

  let saida = '';
  let i = 0;
  for (const caractere of mascara) {
    if (i >= digitos.length) break;
    saida += caractere === '9' ? digitos[i++] : caractere;
  }
  return saida;
}

/**
 * O Brasil não cabe num molde fixo: celular tem 9 dígitos e fixo tem 8, então o
 * hífen muda de lugar conforme o número cresce.
 */
export function formatarTelefone(pais: PaisTelefone, valor: string) {
  return pais.codigo === 'BR' ? mascararTelefoneBR(valor) : aplicarMascara(valor, pais.mascara);
}

/** O que vai para o servidor: sem espaço, sem parêntese, com DDI. */
export function paraE164(pais: PaisTelefone, valor: string) {
  const digitos = valor.replace(/\D/g, '');
  return digitos ? `+${pais.ddi}${digitos}` : '';
}

/**
 * Máscara de digitação para telefone brasileiro: `(11) 98765-4321`.
 *
 * O servidor normaliza qualquer formato para E.164 — a máscara não existe para
 * ele, existe para quem digita: sem ela, cada pessoa escreve de um jeito e
 * ninguém percebe o dígito faltando até a mensagem não chegar.
 *
 * Quem começa com `+` está declarando o DDI, e aí o texto passa intacto:
 * empurrar parênteses num número português produziria um número que não existe
 * em lugar nenhum.
 */
export function mascararTelefoneBR(valor: string) {
  if (valor.trimStart().startsWith('+')) return valor;

  const digitos = valor.replace(/\D/g, '').slice(0, 11);
  if (digitos.length === 0) return '';
  if (digitos.length <= 2) return `(${digitos}`;

  const ddd = digitos.slice(0, 2);
  const numero = digitos.slice(2);
  if (numero.length <= 4) return `(${ddd}) ${numero}`;

  // Celular tem 9 dígitos, fixo tem 8 — o hífen muda de lugar conforme o
  // número cresce, em vez de assumir um dos dois e errar metade das vezes.
  const corte = numero.length > 8 ? 5 : 4;
  return `(${ddd}) ${numero.slice(0, corte)}-${numero.slice(corte)}`;
}
